import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getFilteredRowModel,
    createColumnHelper,
} from '@tanstack/react-table';

import { TableProps } from './types';
import { UserGroup } from '../../../../../../pages/main/management/groups/types';

// Reutiliza los estilos de la tabla de equipos
import {
    ContainerTable,
    TeamsTableContainer,
    TrStyled,
    FiltersContainer,
} from '../TeamsManagementTable/styles';
// El lápiz y la papelera de fila de las tablas de Gestión (Empresas externas,
// Plantillas de recorrido): cuadrados garnet con el icono, sin texto (feedback 2-nov).
import {
    ActionButton,
    ActionCell,
    ActionIcon,
} from '../ExternalCompaniesManagementTable/styles';
import EditIcon from '../../../../../../assets/icons/edit-icon.svg';
import RemoveIcon from '../../../../../../assets/icons/remove-icon.svg';

import {
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../../Typography';
import SearchBar from '../../../../../inputs/SearchBar';
import { textSearchFilter } from '../../../textSearchFilter';

const columnHelper = createColumnHelper<UserGroup>();

export const UserGroupsManagementTable = ({
    data,
    onEditGroup,
    onDeleteGroup,
}: TableProps): JSX.Element => {
    const { t } = useTranslation(['management', 'common']);
    const [globalFilter, setGlobalFilter] = useState('');

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: () => t('management:groups.columns.name'),
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor((row) => row.members.length, {
                id: 'members',
                header: () => t('management:groups.columns.members'),
                // «3 · Anna Puig, Pere Soler +1»: número, dos nombres y el resto en «+N».
                cell: ({ row }) => {
                    const members = row.original.members;
                    if (members.length === 0) return '0';
                    const names = members
                        .slice(0, 2)
                        .map((m) => `${m.name} ${m.surName}`.trim())
                        .join(', ');
                    const rest = members.length > 2 ? ` +${members.length - 2}` : '';
                    return `${members.length} · ${names}${rest}`;
                },
                enableGlobalFilter: false,
            }),
            columnHelper.display({
                id: 'actions',
                header: () => '',
                cell: ({ row }) => (
                    <ActionCell>
                        {/* Tocar la fila también edita, pero no se adivina: el
                            lápiz lo dice (feedback 2-nov). */}
                        <ActionButton
                            type="button"
                            title={t('common:buttons.edit')}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditGroup?.(row.original);
                            }}
                        >
                            <ActionIcon src={EditIcon} alt={t('common:buttons.edit')} />
                        </ActionButton>
                        <ActionButton
                            type="button"
                            title={t('common:buttons.delete')}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteGroup?.(row.original);
                            }}
                        >
                            <ActionIcon src={RemoveIcon} alt={t('common:buttons.delete')} />
                        </ActionButton>
                    </ActionCell>
                ),
            }),
        ],
        [onDeleteGroup, onEditGroup, t]
    );

    const table = useReactTable({
        data: data || [],
        columns,
        state: { globalFilter },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: textSearchFilter(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const onFilter = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalFilter(e.target.value);
    }, []);

    return (
        <ContainerTable>
            <TeamsTableContainer>
                <Typography size="L" variant="semiBold">
                    {t('management:groups.listTitle')}
                </Typography>
                <FiltersContainer>
                    <SearchBar variant="form" onChangeInput={onFilter} />
                </FiltersContainer>
                <TableContainer>
                    <Table>
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </Th>
                                    ))}
                                </tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => (
                                <TrStyled
                                    key={row.id}
                                    style={onEditGroup ? undefined : { cursor: 'default' }}
                                    onClick={() => onEditGroup?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    ))}
                                </TrStyled>
                            ))}
                            {table.getRowModel().rows.length === 0 && (
                                <tr>
                                    <Td colSpan={columns.length}>
                                        <Typography size="S">
                                            {t('common:messages.noResults')}
                                        </Typography>
                                    </Td>
                                </tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            </TeamsTableContainer>
        </ContainerTable>
    );
};
